import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { auth } from '../api';
import type { CurrentUserResponse } from '../api';

export const sessionKey = ['session'] as const;

/**
 * The signed-in user, or null when nobody is signed in.
 *
 * A 401 here is an answer, not a failure — it means "anonymous". Treating it as an error would
 * make every page load look broken to a logged-out visitor.
 */
async function fetchSession(): Promise<CurrentUserResponse | null> {
  const { data, response } = await auth.me();

  if (response?.status === 401) {
    return null;
  }

  return data ?? null;
}

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: fetchSession,
    staleTime: 30_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { email: string; password: string; rememberMe: boolean }) => {
      const { data, error } = await auth.login({ body });
      if (error) {
        throw error;
      }
      return data ?? null;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(sessionKey, user);
      // Everything else was fetched as somebody else, or as nobody.
      void queryClient.invalidateQueries();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await auth.logout();
    },
    onSuccess: () => {
      queryClient.setQueryData(sessionKey, null);
      queryClient.clear();
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: async (body: { email: string; displayName: string; password: string }) => {
      const { data, error } = await auth.register({ body });
      if (error) {
        throw error;
      }
      return data;
    },
  });
}

// Confirming lives in ConfirmEmailPage rather than in a hook here: it is fired from a mount
// effect, which a React Query mutation does not survive in StrictMode. See the note on that page.

export function useResendVerification() {
  return useMutation({
    mutationFn: async (body: { email: string }) => {
      const { error } = await auth.resendVerification({ body });
      if (error) {
        throw error;
      }
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (body: { email: string }) => {
      const { error } = await auth.forgotPassword({ body });
      if (error) {
        throw error;
      }
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (body: { userId: string; token: string; newPassword: string }) => {
      const { error } = await auth.resetPassword({ body });
      if (error) {
        throw error;
      }
    },
  });
}
